import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { AsyncButton, Button, Checkbox, FormField, Input, Label, Modal, TextArea, useAlert, useToast } from "uxp/components";
import { getSchemaFromFields, IDataSource, IQueryDataSource } from "../datasource";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IQuery, IQueryBuilderInstanceProps, ISourceSchema, IView } from "lucy/query-builder";
import { IContextProvider } from "../../../../uxp";
import { IContextData } from "../../../../hooks/useContextData";
import { hasValue } from "../../../../util";
import { getQueryBuilder, IQueryEngine } from "../source-picker-helper";
import { getContextDataSections, getPillConfiguration } from "../../pill-input/PillInput";
import './LucyQueryBuilderSourceEditor.scss'
import { useQEServices } from "./useQEServices";

export interface ILucyQueryBuilderDataSourcePickerProps {
    show: boolean
    selectedSource?: IDataSource;
    context: IContextProvider;
    contextData?: IContextData;
    onClose: () => void;
    onSelect: (source: IDataSource) => void;
    onRemove?: (id: string) => void,
    renderFooterNote?: React.ReactNode,

    // for saving
    enableSave?: boolean // enable search by default and hide option to enable save
    editingQueryId?: string
    onSave?: (source: IDataSource) => Promise<{ status: 'done' | 'error', message: string, queryId?: string }>
    useSavedQueryPipeline?: boolean // this is to replce the pipline with save query when binding data sources - default is true
    canSelectSavedQuerries?: boolean
}

export const LucyQueryBuilderDataSourcePicker: React.FC<ILucyQueryBuilderDataSourcePickerProps> = (props) => {

    const [QueryBuilder, setQueryBuilder] = useState<IQueryEngine>(null)
    const [showHeader, setShowHeader] = useState<boolean>(false)
    const [source, setSource] = useState<IQueryDataSource | null>(null)
    const [view, setView] = useState<'editor' | 'save'>('editor')
    const [showWarning, setShowWarning] = useState(true)
    const [saveQuery, setSaveQuery] = useState(props?.enableSave || false)
    const [enableSave, setEnableSave] = useState(false)

    const queryBuilderRef = useRef<IQueryBuilderInstanceProps>(null)
    const queryEngineServices = useQEServices(props.context)

    const alerts = useAlert()
    const toast = useToast()

    const canSave = (
        enableSave
        && !!props?.onSave && typeof props?.onSave == 'function'
        && (
            !source?.query?.sourceOnly
            || (source?.query?.sourceOnly && !source?.query?.sources?.[0]?.id?.startsWith('query'))
        )
    )

    const isEditing = hasValue(props?.editingQueryId)

    useEffect(() => {
        setView('editor')
        setShowWarning(true)
    }, [props])

    useEffect(() => {
        getQueryBuilderIfAvailable()
        checkIfAbleToSave()
    }, [])

    useEffect(() => {
        setSource({ ...props.selectedSource as IQueryDataSource })
    }, [props?.selectedSource, props?.show])

    function checkIfAbleToSave() {
        props.context.executeService('Lucy', 'Query:All', { max: 1 })
            .then(res => {
                setEnableSave(res?.length > 0)
            })
            .catch(e => {
                setEnableSave(false)
            })
    }

    async function getQueryBuilderIfAvailable() {
        const qb = await getQueryBuilder(props.context)
        setQueryBuilder(qb)
    }

    async function handleCancel() {
        try {
            await queryBuilderRef?.current?.onCancel()
            setSource(prev => ({ ...prev, query: null }))
        }
        catch (e) {

        }

    }

    async function getQuery(): Promise<{ query: IQuery, schema: ISourceSchema } | null> {
        try {
            const { isValid, query, schema, error } = await queryBuilderRef?.current?.onSave()
            if (!isValid || hasValue(error)) {
                toast.error(error || 'Invalid Query')
                return null
            }
            return { query, schema }
        }
        catch (e) {
            console.log('Unable to get the query from query builder. error: ', e)
            toast.error('unable to get the query. something went wrong')
            return null
        }
    }

    async function handleSelect() {
        const _query = await getQuery()
        if (!_query) { setSource(null); setView('editor'); return }

        const { query, schema } = _query

        const _source: IQueryDataSource = Object.assign({}, source);
        _source.type = 'query'
        _source.query = query
        _source.schema = getSchemaFromFields(schema)
        _source.name = getSourceName(_source)

        setSource(_source)
        setView('save')
    }

    async function onCompleteSelection() {
        if (!hasValue(source?.name)) {
            toast.error('Please set a name for the query')
            return
        }

        if (canSave && saveQuery) {
            const { status, message, queryId } = await props.onSave(source)
            if (status == 'error') {
                alerts.show(message)
                return
            }
            // referesh sources
            await queryEngineServices?.refreshDatasources?.()

            toast.success(message)

            if (props?.useSavedQueryPipeline) {
                // get saved query and build updated source 
                const querySource = await queryEngineServices?.getQueryDataSourceById?.(queryId)
                if (querySource) {
                    source.query.sources = [querySource]
                    source.query.steps = []
                    source.query.sourceOnly = true
                }
            }
        }
        props.onSelect(source)
    }

    function handleViewChange(view: IView) {
        // this is query editors view, not the view state in this component 
        setShowHeader(view == 'editor')
    }

    function hasSchemaChanged() {
        const oldSchema = (props?.selectedSource as IQueryDataSource)?.schema || {};
        const newSchema = source.schema || {};

        if (oldSchema?.type !== newSchema?.type) return true;

        function fieldsChanged(oldFields: Record<string, any>, newFields: Record<string, any>) {
            const oldKeys = new Set(Object.keys(oldFields || {}));
            const newKeys = new Set(Object.keys(newFields || {}));

            for (const key of oldKeys) {
                if (!newKeys.has(key)) return true;
            }
            return false;
        }

        if (oldSchema?.type === 'array') {
            return fieldsChanged(oldSchema?.items?.properties, newSchema?.items?.properties);
        }

        if (oldSchema?.type === 'object') {
            return fieldsChanged(oldSchema?.properties, newSchema?.properties);
        }

        return false;
    }

    function renderWarning(canDismiss?: boolean) {
        const canShowWarning = canDismiss ? (hasValue(props?.editingQueryId) && showWarning) : hasValue(props?.editingQueryId)
        if (!canShowWarning) return null

        let message = 'Modifying this saved query will affect all instances where it is used. Proceed with caution.'
        if (!canDismiss && hasSchemaChanged()) {
            message = 'Warning: The output schema has changed. This will affect all instances where it is used. Proceed with caution.'
        }

        return < div className="lcbds-picker-warning-container" >
            <div className="lcbds-picker-warning-container-message">{message}</div>

            {canDismiss ?
                <div className="lcbds-picker-close-button" onClick={() => setShowWarning(false)}>
                    <FontAwesomeIcon icon={['fas', 'times']} />
                </div>
                : null}

        </div >
    }

    function renderSaveInstructions() {
        if (canSave && !isEditing && !props?.enableSave) {
            return <div className="lcbds-picker-saving-instructions-container">
                <div className="lcbds-picker-saving-instructions-title">Save this query for future use </div>
                <div className="lcbds-picker-saving-instructions-message">Check the box below if you want to save this query before completing.</div>

                <Checkbox
                    checked={saveQuery}
                    onChange={setSaveQuery}
                    label="Save Query"
                    type='bordered'
                />
            </div>
        }
        return null
    }

    function renderCompleteButton() {

        const isSaving = canSave && (isEditing || saveQuery || props?.enableSave)

        return <AsyncButton
            icon={isSaving ? 'fas save' : 'fas check-circle'}
            title={isSaving ? 'Save & Complete' : 'Complete'}
            loadingTitle={isSaving ? 'Saving...' : 'Completing...'}
            onClick={onCompleteSelection}
            className="lcbds-picker-primary-button"
        />
    }

    function getSourceName(_source: IQueryDataSource) {
        if (hasValue(_source?.name)) return _source.name
        else if (_source?.query?.sourceOnly) return _source?.query?.sources?.[0]?.name || ''
        return ''
    }

    if (!QueryBuilder) return null
    return <Modal
        title=''
        show={props.show}
        onClose={() => props.onClose()}
        className="lcbds-picker-container-modal"
    >
        <div className={classNames('lcbds-picker-container', { 'has-header': showHeader })}>
            {showHeader ?
                <div className="lcbds-picker-container-header">
                    <div className="lcbds-picker-container-header-title">{view === 'editor' ? 'Build Pipeline' : 'Save Pipeline'}</div>
                    <div className="lcbds-picker-container-header-actions">
                        {view == 'editor' ?
                            <>
                                <Button
                                    icon={'fas chevron-left'}
                                    title="Cancel & Start Fresh"
                                    onClick={handleCancel}
                                    className="lcbds-picker-secondary-button"
                                />
                                <AsyncButton
                                    icon={'fas chevron-right'}
                                    iconPosition='right'
                                    title="Continue"
                                    onClick={handleSelect}
                                    className="lcbds-picker-primary-button"
                                />
                            </>
                            : null}

                        <div className="lcbds-picker-close-button" onClick={() => props.onClose()}>
                            <FontAwesomeIcon icon={['fas', 'times']} />
                        </div>
                    </div>
                </div>
                :
                <div className="lcbds-picker-close-button-container">
                    <div className="lcbds-picker-close-button" onClick={() => props.onClose()}>
                        <FontAwesomeIcon icon={['fas', 'times']} />
                    </div>
                </div>
            }
            <div className={classNames("lcbds-picker-container-body", { 'has-footer-note': !!props.renderFooterNote })}>
                {view == 'editor' ?
                    <div className="lcbds-picker-edit-screen-container">
                        <QueryBuilder
                            initialQuery={source?.query}
                            uxpContext={props.context}
                            onChangeView={handleViewChange}
                            ref={queryBuilderRef}
                            editingQueryId={props?.editingQueryId}
                            externalContextData={{
                                contextDataSections: getContextDataSections(props.contextData, true, false),
                                pillConfiguration: getPillConfiguration()
                            }}
                            canSelectSavedQuerries={props?.canSelectSavedQuerries && canSave}
                        />

                        {renderWarning(true)}

                    </div>
                    :
                    <div className="lcbds-picker-save-screen-container">
                        <div className="lcbds-picker-save-screen">

                            {renderWarning(false)}

                            <div className="lcbds-picker-form">
                                <FormField>
                                    <Label>Name</Label>
                                    <Input
                                        value={source?.name || ''}
                                        onChange={(v) => { setSource(prev => ({ ...prev, name: v })) }}
                                        placeholder="Give a name for the query"
                                    />
                                </FormField>

                                <FormField>
                                    <Label>Description</Label>
                                    <TextArea
                                        value={source?.description || ''}
                                        onChange={(v) => { setSource(prev => ({ ...prev, description: v })) }}
                                    />
                                </FormField>
                            </div>

                            {renderSaveInstructions()}

                            <div className="lcbds-picker-button-row">
                                {renderCompleteButton()}
                            </div>

                        </div>
                    </div>
                }

                {!!props.renderFooterNote
                    ? <div className="lcbds-picker-footer-note-container">{props.renderFooterNote}</div>
                    : null}
            </div>
        </div>

    </Modal>
}

LucyQueryBuilderDataSourcePicker.defaultProps = {
    useSavedQueryPipeline: true
}